import { useEffect, useState } from "react";
import axios from "axios";
import { composeApiURL } from "helpers/utility/axiosHelpers";
import { useGetStats } from "./useGetStats";
import { useUserConfig } from "../useUserConfig";

export enum FlowStatus {
  INIT = "INIT",
  RUNNING = "RUNNING",
  REVIEW = "REVIEW",
  FAILED = "FAILED",
  PAUSED_FOR_REVIEW = "PAUSED_FOR_REVIEW",
  COMPLETED = "COMPLETED",
  RESUME_FLOW = "RESUME_FLOW",
  APPLY_MODEL_TO_DATASET = "APPLY_MODEL_TO_DATASET",
}

type FlowStatusResponse = {
  status: FlowStatus;
};

type Props = {
  datasetId: string;
  flowId: string | null;
  seedId: string | null;
  active: boolean;
  avoidPollStats?: boolean;
};

export const usePollSeedStatusAndStats = ({
  datasetId,
  flowId,
  seedId,
  active,
  avoidPollStats,
}: Props) => {
  const [status, setStatus] = useState<FlowStatus | null>(null);
  const [polling, setPolling] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);

  const { stats, fetchStats } = useGetStats();
  const { refetch: refetchUserConfig } = useUserConfig();

  useEffect(() => {
    if (!active) {
      setStatus(null);
    }
  }, [active]);

  const HOST_URL = process.env.REACT_APP_API_ENDPOINT!;

  const isFinalStatus = (s: FlowStatus) =>
    s === FlowStatus.REVIEW ||
    s === FlowStatus.FAILED ||
    s === FlowStatus.PAUSED_FOR_REVIEW ||
    s === FlowStatus.COMPLETED;

  useEffect(() => {
    if (!active || !flowId) return;

    const statusUrl = composeApiURL(
      HOST_URL,
      `/flywheel/${datasetId}/status/${flowId}`,
    );

    let intervalId: NodeJS.Timeout | null = null;

    const stopPolling = () => {
      if (intervalId) {
        setPolling(false);
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const fetchAndUpdate = async () => {
      try {
        setPolling(true);
        const res = await axios.get<FlowStatusResponse>(statusUrl);
        const currentStatus = res.data.status;
        setStatus(currentStatus);

        if (currentStatus !== FlowStatus.INIT && !avoidPollStats) {
          await fetchStats(datasetId, flowId);
        }

        if (isFinalStatus(currentStatus)) {
          stopPolling();
          if (currentStatus === FlowStatus.COMPLETED) {
            refetchUserConfig();
          }
        }
      } catch (err) {
        setError(err);
        stopPolling();
        refetchUserConfig();
      }
    };

    fetchAndUpdate();
    intervalId = setInterval(fetchAndUpdate, 5000);

    return () => stopPolling();
    // deliberately not adding fetchStats/fetchSeed to deps to avoid infinite re-run
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, datasetId, flowId, seedId, HOST_URL]);

  return {
    status,
    stats,
    error,
    isFinalStatus,
    polling,
  };
};
